import { Injectable, inject, PLATFORM_ID, ProviderToken } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { isPlatformServer } from '@angular/common';
import { CmsService } from './cms.service';
import { CmsPageCopy } from '@models/copy/cms.model';
import { CultureEnum } from '@models/enums/culture.enum';
import { CmsPageEnum } from '@models/copy/cms-page-map.model';

@Injectable({ providedIn: 'root' })
export class CmsCopyResolver implements Resolve<CmsPageCopy | undefined> {
  private cms = inject(CmsService);
  private platformId = inject(PLATFORM_ID);
  // ORIGINAL_URL is provided by the server.ts render providers when running under SSR
  private originalUrl = inject<string | undefined>(
    'ORIGINAL_URL' as unknown as ProviderToken<string | undefined>,
    { optional: true },
  );

  resolve(route: ActivatedRouteSnapshot): Promise<CmsPageCopy | undefined> {
    const path = route.data['cmsPath'] as CmsPageEnum | undefined;
    if (!path) {
      return Promise.resolve(undefined);
    }
    // If we're on the server, only fetch when the incoming request was explicitly
    // for the not-found page (avoid running the resolver for unrelated incoming URLs).
    if (isPlatformServer(this.platformId)) {
      const reqPath = this.originalUrl ? this.originalUrl.split('?')[0] : '';
      if (!reqPath.startsWith('/not-found')) {
        return Promise.resolve(undefined);
      }
    }
    return this.cms
      .getCmsCopy(CultureEnum.EN_US, path)
      .then((copy) =>
        copy && typeof copy === 'object'
          ? (copy.data as CmsPageCopy | undefined)
          : undefined,
      )
      .catch(() => {
        // Gracefully continue if CMS copy fetch fails; page can render without copy
        return undefined;
      });
  }
}
